// src/pages/Reports/ReportIntake.jsx
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  useIntakes,
  useDefaultIntakeId,
  useSubjects,
  useMatrixData,
  aggregateEnrollmentsToMatrix,
  useSingleSubjectData,
} from "../../hooks/useReportIntake";
import { Check, Download } from "lucide-react";

export default function ReportIntake() {
  const [selectedIntakeId, setSelectedIntakeId] = useState("");
  const [selectedSubjectCode, setSelectedSubjectCode] = useState("All");

  // Intakes
  const { data: intakes = [], isLoading, isError, error } = useIntakes();
  const defaultIntakeId = useDefaultIntakeId(intakes);

  // Subjects (for selector and matrix columns)
  const {
    data: subjects = [],
    isLoading: isLoadingSubjects,
    isError: isErrorSubjects,
    error: subjectsError,
  } = useSubjects();

  // Auto-select default intake once loaded
  useEffect(() => {
    if (!selectedIntakeId && !isLoading && intakes.length > 0 && defaultIntakeId) {
      setSelectedIntakeId(defaultIntakeId);
    }
  }, [selectedIntakeId, isLoading, intakes, defaultIntakeId]);

  // Subject options (first = All, then codes from DB)
  const subjectOptions = useMemo(
    () => ["All", ...subjects.map((s) => s.code)],
    [subjects]
  );

  // Resolve the selected subject UUID from the chosen code
  const selectedSubjectId = useMemo(() => {
    if (selectedSubjectCode === "All") return null;
    const found = subjects.find((s) => s.code === selectedSubjectCode);
    return found ? found.id : null;
  }, [subjects, selectedSubjectCode]);

  // Matrix data when Subject = All
  const {
    data: matrixRaw = [],
    isLoading: isLoadingMatrix,
    isError: isErrorMatrix,
    error: matrixError,
  } = useMatrixData(
    selectedIntakeId && selectedSubjectCode === "All" ? selectedIntakeId : null
  );

  // Build subject columns for matrix: codes sorted
  const matrixSubjectCodes = useMemo(
    () => subjects.map((s) => s.code).sort(),
    [subjects]
  );

  // Aggregate per student
  const matrixRows = useMemo(() => {
    if (selectedSubjectCode !== "All" || !selectedIntakeId) return [];
    return aggregateEnrollmentsToMatrix({
      rows: matrixRaw,
      subjectCodes: matrixSubjectCodes,
    });
  }, [matrixRaw, matrixSubjectCodes, selectedIntakeId, selectedSubjectCode]);

  // Single-subject data (now filtered by subject_id UUID)
  const {
    data: singleRows = [],
    isLoading: isLoadingSingle,
    isError: isErrorSingle,
    error: singleError,
  } = useSingleSubjectData(selectedIntakeId, selectedSubjectId);

  // Helpers for CSV export
  const intakeName = useMemo(
    () => intakes.find((i) => i.id === selectedIntakeId)?.name || "",
    [intakes, selectedSubjectId, selectedIntakeId]
  );
  const normalizedIntake = (intakeName || "").replaceAll("/", "_");

  const todayKL = useMemo(() => {
    const y = new Intl.DateTimeFormat("en-GB", {
      year: "numeric",
      timeZone: "Asia/Kuala_Lumpur",
    }).format(new Date());
    const m = new Intl.DateTimeFormat("en-GB", {
      month: "2-digit",
      timeZone: "Asia/Kuala_Lumpur",
    }).format(new Date());
    const d = new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      timeZone: "Asia/Kuala_Lumpur",
    }).format(new Date());
    return `${y}${m}${d}`; // YYYYMMDD
  }, []);

  const csvEscape = (val) => {
    const s = val == null ? "" : String(val);
    if (/[",\n]/.test(s)) {
      return `"${s.replace(/"/g, '""')}"`;
    }
    return s;
  };

  const buildMatrixCsv = () => {
    const headers = [
      "No.",
      "Student Name",
      "NRIC/Passport",
      "Email Address",
      "No. of Subjects Enrolled in Intake",
      ...matrixSubjectCodes,
    ];
    const lines = [headers.map(csvEscape).join(",")];

    matrixRows.forEach((row, idx) => {
      const baseCols = [
        idx + 1,
        row.full_name,
        row.nric_passport, // ⬅️ changed from contact_no
        row.email,
        row.countInIntake,
      ];
      const flagCols = matrixSubjectCodes.map((code) =>
        row.subjectFlags[code] ? "Y" : ""
      );
      lines.push([...baseCols, ...flagCols].map(csvEscape).join(","));
    });

    const csv = lines.join("\n");
    const filename = `excel_rism_${normalizedIntake}_${todayKL}.csv`;
    return { csv, filename };
  };

  const buildSingleCsv = () => {
    const headers = ["No.", "Student Name", "NRIC/Passport", "Email Address"];
    const lines = [headers.map(csvEscape).join(",")];

    singleRows.forEach((row, idx) => {
      const cols = [idx + 1, row.full_name, row.nric_passport, row.email]; // ⬅️ nric_passport
      lines.push(cols.map(csvEscape).join(","));
    });

    const subjectCode = selectedSubjectCode;
    const filename = `excel_rism_${subjectCode}_${normalizedIntake}_${todayKL}.csv`;
    return { csv: lines.join("\n"), filename };
  };

  const hasMatrix =
    selectedIntakeId && selectedSubjectCode === "All" && matrixRows.length > 0;
  const hasSingle =
    selectedIntakeId && selectedSubjectCode !== "All" && singleRows.length > 0;
  const exportEnabled = Boolean(hasMatrix || hasSingle);

  const handleExportCsv = () => {
    const { csv, filename } =
      selectedSubjectCode === "All" ? buildMatrixCsv() : buildSingleCsv();

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  // Small chip component for the header filters
  const Chip = ({ children, tone = "indigo" }) => (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs
      ${tone === "indigo" ? "border-indigo-200 bg-indigo-50 text-indigo-700" : ""}
      ${tone === "sky" ? "border-sky-200 bg-sky-50 text-sky-700" : ""}
      ${tone === "slate" ? "border-slate-200 bg-slate-50 text-slate-700" : ""}`}
    >
      {children}
    </span>
  );

  return (
    <div className="p-2 md:p-2">
      {/* Header card with gradient accent */}
      <div className="mb-4">
        <div className="relative overflow-hidden rounded-xl border border-slate-200 shadow-sm">
          <div className="h-2 w-full bg-gradient-to-r from-indigo-500 via-sky-400 to-cyan-400" />
          <div className="bg-white p-4 md:p-5">
            <h1 className="text-xl font-semibold text-slate-800">
              Intake Report
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              Filter by intake and subject. Click a student to view details.
            </p>

            {/* Live filter chips */}
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <Chip tone="indigo">Intake: {intakeName || "-"}</Chip>
              <Chip tone="sky">
                Subject:{" "}
                {selectedSubjectCode === "All"
                  ? "All"
                  : subjects.find((s) => s.code === selectedSubjectCode)?.name ||
                    selectedSubjectCode}
              </Chip>
            </div>

            {/* Filter controls */}
            <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-[14rem,18rem,1fr] md:items-end">
              {/* Intake */}
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  Intake
                </label>
                <select
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-sky-300"
                  value={selectedIntakeId}
                  onChange={(e) => setSelectedIntakeId(e.target.value)}
                  disabled={isLoading || isError}
                >
                  {isLoading && <option>Loading…</option>}
                  {!isLoading && !intakes.length && <option>No intakes found</option>}
                  {!isLoading &&
                    intakes.map((i) => (
                      <option key={i.id} value={i.id}>
                        {i.name}
                      </option>
                    ))}
                </select>
                {isError && (
                  <p className="mt-1 text-xs text-red-600">
                    Failed to load intakes: {error?.message || "Unknown error"}
                  </p>
                )}
              </div>

              {/* Subject */}
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  Subject
                </label>
                <select
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-sky-300"
                  value={selectedSubjectCode}
                  onChange={(e) => setSelectedSubjectCode(e.target.value)}
                  disabled={isLoadingSubjects || isErrorSubjects}
                >
                  {isLoadingSubjects && <option>Loading…</option>}
                  {!isLoadingSubjects &&
                    subjectOptions.map((code) => (
                      <option key={code} value={code}>
                        {code === "All"
                          ? "All"
                          : subjects.find((s) => s.code === code)?.name || code}
                      </option>
                    ))}
                </select>
                {isErrorSubjects && (
                  <p className="mt-1 text-xs text-red-600">
                    Failed to load subjects: {subjectsError?.message || "Unknown error"}
                  </p>
                )}
              </div>

              {/* Export */}
              <div className="flex justify-start md:justify-end">
                <button
                  type="button"
                  onClick={handleExportCsv}
                  disabled={!exportEnabled}
                  title="Export what’s on screen"
                  className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium shadow-sm
                    ${exportEnabled
                      ? "bg-sky-600 text-white hover:bg-sky-700"
                      : "bg-slate-100 text-slate-400 cursor-not-allowed"}`}
                >
                  <Download className="h-4 w-4" strokeWidth={2.5} />
                  Export CSV
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content Card */}
      <div className="mt-4 bg-white border border-slate-200 rounded-xl shadow-sm">
        <div className="p-3 md:p-4">
          {/* Subject = All → Matrix */}
          {selectedIntakeId && selectedSubjectCode === "All" && (
            <>
              {isLoadingMatrix ? (
                <div className="text-xs text-slate-500">Loading matrix…</div>
              ) : isErrorMatrix ? (
                <div className="text-xs text-red-600">
                  Failed to load data: {matrixError?.message || "Unknown error"}
                </div>
              ) : matrixRows.length === 0 ? (
                <div className="text-xs text-slate-500">
                  No students for this intake yet.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-[1400px] w-full text-sm">
                    <thead className="sticky top-0 bg-slate-50">
                      <tr className="text-left text-slate-600 border-b">
                        <th className="py-2 pr-2">No.</th>
                        <th className="py-2 pr-2">Student Name</th>
                        <th className="py-2 pr-2">NRIC/Passport</th>{/* ⬅️ changed */}
                        <th className="py-2 pr-2">Email Address</th>
                        <th className="py-2 pr-2"># of Subjects</th>
                        {matrixSubjectCodes.map((code) => (
                          <th key={code} className="py-1 px-1.5 text-center">
                            <span className="rounded bg-sky-50 px-1.5 py-0.5 text-sky-700 border border-sky-100 text-[11px]">
                              {code}
                            </span>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {matrixRows.map((row, idx) => (
                        <tr
                          key={row.id}
                          className={`border-b last:border-0 ${
                            idx % 2 === 0 ? "bg-white" : "bg-slate-50/40"
                          } hover:bg-sky-50/60`}
                        >
                          <td className="py-2 pr-2">{idx + 1}</td>
                          <td className="py-2 pr-2">
                            <Link
                              to={{ pathname: `/students/${row.id}`, search: "?from=report" }}
                              state={{ from: "report" }}
                              className="font-medium text-slate-800 hover:text-sky-700 hover:underline"
                            >
                              {row.full_name}
                            </Link>
                          </td>
                          <td className="py-2 pr-2">{row.nric_passport}</td>{/* ⬅️ changed */}
                          <td className="py-2 pr-2">{row.email}</td>
                          <td className="py-2 pr-2 text-center">{row.countInIntake}</td>
                          {matrixSubjectCodes.map((code) => (
                            <td
                              key={code}
                              className="py-2 px-1.5 text-center"
                              title={row.subjectFlags[code] ? "Enrolled" : "Not enrolled"}
                            >
                              {row.subjectFlags[code] ? (
                                <span className="inline-flex items-center justify-center rounded bg-emerald-50 p-0.5 text-emerald-700 border border-emerald-100">
                                  <Check size={12} strokeWidth={2.5} />
                                </span>
                              ) : (
                                ""
                              )}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="mt-2 text-[11px] text-slate-500">
                    Showing {matrixRows.length} students • Intake: {intakeName || "-"} •
                    Subject: All
                  </div>
                </div>
              )}
            </>
          )}

          {/* Subject = specific code → Simple List */}
          {selectedIntakeId && selectedSubjectCode !== "All" && (
            <>
              {isLoadingSingle ? (
                <div className="text-xs text-slate-500">Loading students…</div>
              ) : isErrorSingle ? (
                <div className="text-xs text-red-600">
                  Failed to load data: {singleError?.message || "Unknown error"}
                </div>
              ) : singleRows.length === 0 ? (
                <div className="text-xs text-slate-500">
                  No students found for this subject in the selected intake.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-[600px] w-full text-xs">
                    <thead className="sticky top-0 bg-slate-50">
                      <tr className="text-left text-slate-600 border-b">
                        <th className="py-2 pr-2">No.</th>
                        <th className="py-2 pr-2">Student Name</th>
                        <th className="py-2 pr-2">NRIC/Passport</th>{/* ⬅️ changed */}
                        <th className="py-2 pr-2">Email Address</th>
                      </tr>
                    </thead>
                    <tbody>
                      {singleRows.map((row, idx) => (
                        <tr
                          key={row.id}
                          className={`border-b last:border-0 ${
                            idx % 2 === 0 ? "bg-white" : "bg-slate-50/40"
                          } hover:bg-sky-50/60`}
                        >
                          <td className="py-2 pr-2">{idx + 1}</td>
                          <td className="py-2 pr-2">
                            <Link
                              to={{ pathname: `/students/${row.id}`, search: "?from=report" }}
                              state={{ from: "report" }}
                              className="font-medium text-slate-800 hover:text-sky-700 hover:underline"
                            >
                              {row.full_name}
                            </Link>
                          </td>
                          <td className="py-2 pr-2">{row.nric_passport}</td>{/* ⬅️ changed */}
                          <td className="py-2 pr-2">{row.email}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="mt-2 text-[11px] text-slate-500">
                    Showing {singleRows.length} students • Intake: {intakeName || "-"} •
                    Subject:{" "}
                    {subjects.find((s) => s.code === selectedSubjectCode)?.name ||
                      selectedSubjectCode}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Placeholder when no intake selected */}
          {!selectedIntakeId && (
            <div className="text-xs text-slate-500">
              Select an intake to view enrollment data.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
